/**
 * @description Surcharge consent modal — shown after server-side surcharge check
 * confirms a credit card surcharge applies. Displays rate, subtotal, surcharge
 * amount, and adjusted total. User must accept or decline before payment proceeds.
 *
 * @dependencies
 * - @headlessui/react: Dialog, Transition
 */

import { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';

function SurchargeConsentModal({
  show = false,
  surchargeInfo,
  onAccept,
  onDecline,
  formatCurrency
}) {
  if (!surchargeInfo) return null;

  const subtotalCents = (surchargeInfo.adjustedTotalCents || 0) - (surchargeInfo.surchargeAmountCents || 0);

  return (
    <Transition.Root show={show} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onDecline}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative w-full max-w-md transform overflow-hidden rounded-xl bg-white shadow-2xl transition-all">
                <div className="px-6 pt-6 pb-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100">
                      <svg className="h-5 w-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                    </div>
                    <Dialog.Title className="text-lg font-semibold text-gray-900">
                      Credit Card Surcharge
                    </Dialog.Title>
                  </div>
                </div>

                <div className="px-6 pb-4">
                  <p className="text-sm text-gray-600">
                    A surcharge of{' '}
                    <span className="font-semibold text-gray-900">
                      {((surchargeInfo.surchargeRate || 0) * 100).toFixed(2)}%
                    </span>
                    {' '}will be applied to this credit card transaction.
                    If you do not wish to proceed, click Back and pay by alternate means.
                  </p>

                  <div className="mt-4 rounded-lg bg-gray-50 border border-gray-200 p-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Subtotal</span>
                      <span className="text-gray-900">
                        {formatCurrency(subtotalCents / 100)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">
                        Surcharge ({((surchargeInfo.surchargeRate || 0) * 100).toFixed(2)}%)
                      </span>
                      <span className="font-medium text-amber-600">
                        +{formatCurrency(surchargeInfo.surchargeAmountCents / 100)}
                      </span>
                    </div>
                    <div className="border-t border-gray-200 pt-2 flex justify-between text-sm">
                      <span className="font-medium text-gray-900">Total</span>
                      <span className="font-semibold text-gray-900">
                        {formatCurrency(surchargeInfo.adjustedTotalCents / 100)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="px-6 pb-6 flex gap-3">
                  <button
                    type="button"
                    onClick={onDecline}
                    className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-200"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={onAccept}
                    className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-brand hover:bg-brand-hover rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand"
                  >
                    Process Payment {formatCurrency(surchargeInfo.adjustedTotalCents / 100)}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

export default SurchargeConsentModal;
